<template>
  <div class="bg-white">
    <div v-if="withTitle" class="card-title">
      <h1 class="font-weight-black">
        <div v-if="$slots.header" class="card-header">
          <slot name="header" />
        </div>
        <template v-else>
          <v-icon large class="text-black"> mdi-cash </v-icon>
          SOLICITUDES DE ACTUALIZACIÓN DE PAGARÉS
        </template>
      </h1>
    </div>
    <v-card class="pa-4 ma-4">
      <v-data-table
        item-class="table-heading"
        :loading="loading"
        :headers="headers"
        :items="items"
        :search="search"
      >
        <template #top>
          <div v-if="withFilters" class="d-flex justify-center align-center">
            <v-progress-linear
              v-if="statuses.loading"
              class="px-1 py-2"
              indeterminate
            />
            <template v-else>
              <b class="text-no-wrap">Filtrar por:</b>
              <v-radio-group
                v-model="status"
                :disabled="loading || statuses.loading"
                row
              >
                <v-radio
                  v-for="item in statuses.statuses"
                  :key="item.value"
                  class="mx-2 w-25"
                  color="orange"
                  :label="item.description"
                  :value="item.key"
                />
              </v-radio-group>
            </template>
          </div>
          <search-input
            v-if="searchable"
            v-model="search"
            :disabled="loading || statuses.loading"
          />
          <template v-if="exportable">
            <div class="d-flex justify-end align-center">
              <v-btn
                :loading="excel.exporting.exporting"
                :disabled="excel.exporting.exporting || loading"
                color="success"
                class="ma-2 white--text"
                @click="exportToExcel"
              >
                <v-icon left dark> mdi-download-box </v-icon> Excel
              </v-btn>
            </div>
          </template>
        </template>

        <template #[`item.customer_id`]="{ item }">
          <router-link
            v-if="withCustomerAction"
            class="text-decoration-underli"
            :to="{
              name: 'CreditDetail',
              params: { customerId: item.person_id },
            }"
          >
            {{ item.customer_id }}
          </router-link>
          <span v-else>
            {{ item.customer_id }}
          </span>
        </template>
        <template #[`item.customer_name`]="{ item }">
          <info-tooltip
            :tooltip-text="item.customer_name"
            :text="item.customer_name_2"
          />
        </template>

        <template #[`item.bank_guarantee_name`]="{ item }">
          <info-tooltip
            :tooltip-text="item.bank_guarantee_name ?? 'Sin aval'"
            :text="item.bank_guarantee_name_2"
          />
        </template>

        <template #[`item.promissory_note_amount`]="{ item }">
          {{ formatNumber(item.promissory_note_amount, '$', 0) }}
        </template>
        <template #[`item.credit_line`]="{ item }">
          {{ formatNumber(item.credit_line, '$', 0) }}
        </template>
        <template #[`item.credit_line_amount_approved`]="{ item }">
          {{ formatNumber(item.credit_line_amount_approved, '$', 0) }}
        </template>
        <template #[`item.credit_line_amount_requested`]="{ item }">
          {{ formatNumber(item.credit_line_amount_requested, '$', 0) }}
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip :color="item.status.color" class="ma-2 text-white" small>
            {{ item.status.description ?? 'sin descripción' }}
          </v-chip>
        </template>
        <template #[`item.comments`]="{ item }">
          <info-tooltip
            v-if="!item.is_promissory_note"
            tooltip-text="Carta no pagaré"
            icon="mdi-information"
          />
          <v-btn color="info" small icon @click="showComments(item.comments)">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>
        <template #[`item.files`]="{ item }">
          <template v-for="(doc, i) in docs">
            <a
              v-if="item.files && item.files[doc.value]"
              :key="i"
              href="#"
              @click.prevent="openFile(item.files[doc.value])"
            >
              <info-tooltip
                :icon-color="doc.color"
                icon="mdi-file-account"
                :tooltip-text="doc.text"
              />
            </a>
          </template>
        </template>
        <template #[`item.actions`]="{ item }">
          <div v-if="item.actions" class="text-nowrap">
            <template v-if="isStatusEditable">
              <template v-if="item.complete_request">
                <v-btn
                  color="success"
                  fab
                  x-small
                  dark
                  class="mr-1"
                  :loading="loading"
                  @click="$emit('markedAsComplete', item)"
                >
                  <info-tooltip
                    tooltip-text="Terminar solicitud"
                    icon-color="white"
                    icon="mdi-check"
                  />
                </v-btn>
                <v-btn
                  color="error"
                  fab
                  x-small
                  dark
                  :disabled="loading"
                  @click="rejectPromissory(item)"
                >
                  <info-tooltip
                    tooltip-text="Rechazar solicitud"
                    icon-color="white"
                    icon="mdi-close"
                  />
                </v-btn>
              </template>

              <v-btn
                v-else
                color="orange"
                :loading="loading"
                fab
                x-small
                dark
                class="mr-1"
                @click="$emit('editPromissory', item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <template v-if="item.managerOptions">
              <v-btn
                color="success"
                fab
                x-small
                dark
                class="mr-1"
                :loading="loading"
                @click="approvePromissory(item)"
              >
                <v-icon>mdi-check</v-icon>
              </v-btn>
              <v-btn
                color="error"
                fab
                x-small
                dark
                :loading="loading"
                @click="rejectPromissory(item)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <template v-if="item.sellerOptions">
              <v-btn
                color="error"
                fab
                x-small
                dark
                class="mr-1"
                :loading="loading"
                @click="$emit('destroyPromissory', item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
              <v-btn
                color="orange"
                fab
                x-small
                dark
                :loading="loading"
                @click="editPromissory(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </div>
        </template>
      </v-data-table>
    </v-card>
    <comments v-model="observations.modal" :comments="observations.items" />
  </div>
</template>

<script>
  import axios from '@/plugins/axios';
  import numbersFormats from '@/mixins/numbersFormats';
  import { DOCUMENTS } from '../constants/requerimentDocuments';
  import { useExcelExport } from '@/utilities/useExcelExport';

  import InfoTooltip from '@/components/tooltips/InfoTooltip.vue';
  import Comments from './Comments.vue';
  import SearchInput from '@/components/SearchInput.vue';

  export default {
    name: 'PromissoryNoteTable',
    components: { Comments, InfoTooltip, SearchInput },
    mixins: [numbersFormats],
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      headers: {
        type: Array,
        default: () => [
          { text: 'Fecha (dd/mm/aaaa)', value: 'created' },

          { text: 'Nº Cliente', value: 'customer_id' },
          {
            text: 'Nombre del cliente',
            value: 'customer_name',
            cellClass: 'text-nowrap ',
          },
          {
            text: 'Aval',
            align: 'start',
            sortable: false,
            cellClass: 'text-nowrap',
            value: 'bank_guarantee_name',
          },

          {
            text: 'Firma de pagaré',
            align: 'start',
            sortable: false,
            cellClass: 'text-nowrap',
            value: 'promissory_note_date',
          },
          {
            text: 'Pagaré',
            align: 'start',
            sortable: false,
            value: 'promissory_note_amount',
          },
          {
            text: 'Línea de credito disponible',
            align: 'start',
            sortable: false,
            value: 'credit_line',
          },
          {
            text: 'L. Credito solicitada',
            align: 'start',
            sortable: false,
            value: 'credit_line_amount_requested',
          },

          { text: 'Estatus', value: 'status' },
          { text: 'Tipo de solicitud', value: 'request_type.description' },
          {
            text: 'Observaciones',
            value: 'comments',
            align: 'center',
          },
          { text: 'Archivos', value: 'files', cellClass: 'text-nowrap' },
          { text: 'Acciones', value: 'actions' },
        ],
      },
      role: {
        type: String,
        required: false,
        default: '',
      },
      exportable: Boolean,
      withFilters: Boolean,
      isStatusEditable: Boolean,
      withCustomerAction: Boolean,
      withTitle: Boolean,
      loading: Boolean,
      searchable: Boolean,
    },
    data: () => ({
      excel: null,
      search: '',
      statuses: {
        loading: false,
        statuses: [],
      },
      status: '',
      observations: {
        items: [],
        modal: false,
      },
      observationsModal: false,
    }),
    computed: {
      docs() {
        return DOCUMENTS;
      },
    },
    watch: {
      status(newValue) {
        this.$emit('statusHasChanged', newValue);
      },
    },
    mounted() {
      if (this.withFilters) {
        this.loadCustomerStatus();
      }
    },
    created() {
      this.excel = useExcelExport();
    },
    methods: {
      async exportToExcel() {
        let headers = [...this.headers];
        //add document headers
        //find value: 'status' in headers and update as status.description
        const statusIndex = headers.findIndex((h) => h.value === 'status');
        headers[statusIndex].value = 'status.description';
        Object.values(this.docs).forEach((doc) => {
          headers.push({
            text: doc.text,
            value: `files.${doc.value}`,
            exportable: true,
          });
        });

        await this.excel.exportExcel({
          headers: headers,
          items: this.items,
          filename: 'pagarés.xlsx',
        });
      },
      editPromissory(item) {
        this.$emit('editMode', item);
      },
      async loadCustomerStatus() {
        try {
          this.statuses.loading = true;
          const response = await axios.get(
            '/promissory-notes/requests/status-by-role',
            {
              params: {
                role: this.role,
              },
            }
          );
          this.statuses.statuses = response.data.data;
          this.status = this.statuses.statuses[0].key;
        } catch (error) {
          this.$swal.fire({
            title: 'Ocurrio un error al recuperar los estatus',
            body: error,
          });
        }
        this.statuses.loading = false;
      },
      showComments(item) {
        this.observations.items = item;
        this.observations.modal = true;
      },
      openFile(file) {
        window.open(`${this.$filesUrl}/${file}`, '_blank');
      },
      approvePromissory(item) {
        this.$emit('approvePromissory', item);
      },
      rejectPromissory(item) {
        this.$emit('rejectPromissory', item);
      },
    },
  };
</script>

<style>
  .card-title {
    padding-left: 3em;
    padding-right: 3em;
    margin-top: 1em;
    margin-bottom: 1em;
  }
  .card-title h1 {
    border: 4px solid #000;
    border-radius: 5px;
    font-size: 1.2em;
    padding-top: 0.3em;
    padding-bottom: 0.2em;
  }
</style>
